import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Users, BarChart, Target, Search } from 'lucide-react';
import { services } from '../mock/data';

const Solutions = () => {
  const getServiceIcon = (serviceId) => {
    const iconMap = {
      'ai-consulting': Brain,
      'contactability': Users,
      'broker-analytics': BarChart,
      'campaign-solutions': Target,
      'unclaimed-funds': Search
    };
    return iconMap[serviceId] || Brain;
  };

  return (
    <div className="dark-theme pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="display-large text-white mb-6">
              Intelligent AI Solutions That Deliver{' '}
              <span className="text-bluemp-400">Measurable Results</span>
            </h1>
            <p className="body-large text-gray-300 max-w-3xl mx-auto mb-8">
              We don't just implement AI technology – we craft tailored solutions that address your specific business challenges while maintaining the highest standards of integrity and compliance.
            </p>
            <Link to="/contact" className="btn-primary">
              Discuss Your Needs
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => {
              const IconComponent = getServiceIcon(service.id);
              return (
                <div key={service.id} className="group dark-hover">
                  <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20 h-full">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-bluemp-400/20 rounded-lg flex items-center justify-center">
                        <IconComponent size={32} className="text-bluemp-400" />
                      </div>
                      <div>
                        <h3 className="heading-2 text-white group-hover:text-bluemp-400 transition-colors">
                          {service.title}
                        </h3>
                        <p className="body-small text-bluemp-400">{service.subtitle}</p>
                      </div>
                    </div>

                    <p className="body-medium text-gray-300 mb-6">
                      {service.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="heading-3 text-white mb-4">Core Capabilities</h4>
                      <ul className="space-y-2">
                        {service.capabilities.map((capability, capIndex) => (
                          <li key={capIndex} className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-bluemp-400 rounded-full"></div>
                            <span className="body-small text-gray-300">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h4 className="heading-3 text-white mb-4">Key Benefits</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, benIndex) => (
                          <li key={benIndex} className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-bluemp-400 rounded-full"></div>
                            <span className="body-small text-gray-300">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link 
                      to={`/solutions/${service.id}`}
                      className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors font-semibold"
                    >
                      Learn More <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Our Solution Approach
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Every AI solution follows our proven methodology to ensure successful implementation and measurable outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Discovery & Analysis",
                description: "Deep dive into your business challenges, data landscape, and organizational readiness for AI implementation."
              },
              {
                step: "02", 
                title: "Strategy & Design",
                description: "Develop comprehensive AI roadmap with clear KPIs, risk mitigation strategies, and implementation timeline."
              },
              {
                step: "03",
                title: "Implementation",
                description: "Execute solution with continuous monitoring, testing, and optimization to ensure optimal performance."
              },
              {
                step: "04",
                title: "Support & Scale",
                description: "Ongoing support, training, and scaling guidance to maximize long-term value and adoption."
              }
            ].map((phase, index) => (
              <div key={index} className="text-center group dark-hover">
                <div className="w-16 h-16 bg-bluemp-400/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="heading-2 text-bluemp-400">{phase.step}</span>
                </div>
                <h3 className="heading-3 text-white mb-4">{phase.title}</h3>
                <p className="body-small text-gray-300">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Explore AI Solutions for Your Business?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Every successful AI implementation starts with understanding your unique challenges. Let's discuss how our solutions can drive your business forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Book Discovery Call
              <ArrowRight size={20} />
            </Link>
            <Link to="/ai-learning" className="btn-secondary">
              Explore AI Learning
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;